import React, { useState } from 'react';
import { 
  Accessibility, 
  Eye, 
  Type, 
  Move, 
  Volume2, 
  Keyboard,
  Plus,
  Minus,
  RotateCcw,
  X,
  Settings
} from 'lucide-react';
import { useAccessibility } from './use-accessibility';

interface AccessibilityPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccessibilityPanel: React.FC<AccessibilityPanelProps> = ({ isOpen, onClose }) => {
  const {
    highContrast,
    toggleHighContrast,
    fontSize,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
    reducedMotion,
    toggleReducedMotion,
    screenReader,
    toggleScreenReader,
    keyboardNavigation,
  } = useAccessibility();

  const [activeTab, setActiveTab] = useState<'visual' | 'audio' | 'navigation'>('visual');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Accessibility className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Configurações de Acessibilidade
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Fechar painel de acessibilidade"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('visual')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'visual'
                ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Eye className="w-4 h-4 inline mr-2" />
            Visual
          </button>
          <button
            onClick={() => setActiveTab('audio')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'audio'
                ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Volume2 className="w-4 h-4 inline mr-2" />
            Áudio
          </button>
          <button
            onClick={() => setActiveTab('navigation')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'navigation'
                ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Keyboard className="w-4 h-4 inline mr-2" />
            Navegação
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Visual Tab */}
          {activeTab === 'visual' && (
            <div className="space-y-4">
              {/* Alto Contraste */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Alto Contraste</h3>
                  <p className="text-xs text-gray-500">
                    Melhora a visibilidade do texto e elementos
                  </p>
                </div>
                <button
                  onClick={toggleHighContrast}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    highContrast ? 'bg-orange-600' : 'bg-gray-200'
                  }`}
                  aria-label={`${highContrast ? 'Desativar' : 'Ativar'} alto contraste`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      highContrast ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Tamanho da Fonte */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-900">Tamanho da Fonte</h3>
                  <span className="text-xs text-gray-500">{fontSize}%</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={decreaseFontSize}
                    className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                    aria-label="Diminuir tamanho da fonte"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-orange-600 h-2 rounded-full transition-all"
                      style={{ width: `${((fontSize - 80) / 120) * 100}%` }}
                    />
                  </div>
                  <button
                    onClick={increaseFontSize}
                    className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                    aria-label="Aumentar tamanho da fonte"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <button
                    onClick={resetFontSize}
                    className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                    aria-label="Resetar tamanho da fonte"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Movimento Reduzido */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Movimento Reduzido</h3>
                  <p className="text-xs text-gray-500">
                    Reduz animações e transições
                  </p>
                </div>
                <button
                  onClick={toggleReducedMotion}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    reducedMotion ? 'bg-orange-600' : 'bg-gray-200'
                  }`}
                  aria-label={`${reducedMotion ? 'Desativar' : 'Ativar'} movimento reduzido`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      reducedMotion ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Audio Tab */}
          {activeTab === 'audio' && (
            <div className="space-y-4">
              {/* Screen Reader */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Navegação por Voz</h3>
                  <p className="text-xs text-gray-500">
                    Ativa funcionalidades para leitores de tela
                  </p>
                </div>
                <button
                  onClick={toggleScreenReader}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    screenReader ? 'bg-orange-600' : 'bg-gray-200'
                  }`}
                  aria-label={`${screenReader ? 'Desativar' : 'Ativar'} navegação por voz`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      screenReader ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Audio Descriptions */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Dicas para Navegação por Teclado
                </h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Use Tab para navegar entre elementos</li>
                  <li>• Use Enter ou Espaço para ativar botões</li>
                  <li>• Use setas para navegar em listas</li>
                  <li>• Use Escape para fechar modais</li>
                </ul>
              </div>
            </div>
          )}

          {/* Navigation Tab */}
          {activeTab === 'navigation' && (
            <div className="space-y-4">
              {/* Keyboard Navigation Status */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Navegação por Teclado</h3>
                  <p className="text-xs text-gray-500">
                    Status da navegação por teclado
                  </p>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  keyboardNavigation
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {keyboardNavigation ? 'Ativa' : 'Inativa'}
                </div>
              </div>

              {/* Focus Indicators */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Indicadores de Foco
                </h4>
                <p className="text-xs text-gray-600 mb-3">
                  Quando a navegação por teclado está ativa, os elementos focados são destacados.
                </p>
                <div className="space-y-2">
                  <button className="w-full p-2 text-left text-xs bg-white border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent">
                    Exemplo de botão com foco
                  </button>
                  <input
                    type="text"
                    placeholder="Exemplo de input com foco"
                    className="w-full p-2 text-xs border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Keyboard Shortcuts */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Atalhos de Teclado
                </h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Alt + A:</span>
                    <span>Abrir painel de acessibilidade</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Alt + H:</span>
                    <span>Ir para página inicial</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Alt + S:</span>
                    <span>Buscar serviços</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Alt + C:</span>
                    <span>Meus agendamentos</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500 text-center">
            <p>Use Alt + A para abrir este painel rapidamente</p>
            <p className="mt-1">Configurações são salvas automaticamente</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityPanel; 